/**
 * The shared Meridian ``<DataTable>`` (ADR-038 §Decision 5, Phase E).
 *
 * A fully-controlled, server-driven results table: multi-column sort with
 * per-direction chevrons and sort ordinals, drag-resize on column
 * boundaries, native-HTML5 drag reorder by header, column visibility, and
 * offset pagination — all rendered in Meridian's own markup with
 * Meridian's tokens and Meridian's ARIA.
 *
 * # What the library does and does not do
 *
 * ``@tanstack/react-table`` (MIT, headless — ADR-038 §Implementation
 * Notes) supplies the state machinery *inside* this component: the column
 * model, the visible/ordered leaf resolution, the column-sizing math and
 * its mouse-and-touch resize handler, and the sorted-state accessors used
 * for the chevron indicator and ordinal badge. It renders no DOM and
 * ships no styles, so it creates no visual or structural coupling — the
 * markup, the operator-tooling register, the ``aria-sort`` semantics, and
 * the keyboard model below are all ours. The sort *gesture* is Meridian's
 * too: see :func:`nextSortState` for why the library's own
 * none→asc→desc→none cycle is not used.
 *
 * # Controlled by design
 *
 * Every piece of table chrome — sort, order, visibility, sizing — is a
 * prop, because the Query panel persists it per data type into the
 * ADR-033 Profile document (D13) and sends the sort to the BFF, which
 * computes the order across the whole result set (D7). The table holds no
 * chrome state of its own; the one exception is the transient
 * sort-cap advisory, which is presentation, not chrome.
 *
 * # Accessibility (REQ-UI-059)
 *
 * - A ``<caption>`` names the table for screen readers.
 * - Sortable ``<th>`` carry ``aria-sort``; each chevron is a real
 *   ``<button>`` (so Enter / Space activate natively) with an explicit
 *   ``aria-label`` and ``aria-pressed``.
 * - Each resize handle is a focusable ``separator`` operable with
 *   Arrow keys, so column width is reachable without a pointer.
 * - Header drag-reorder is pointer-only by nature; the keyboard path is
 *   the Move left / Move right actions in
 *   :func:`DataTableColumnMenu`.
 * - Activatable rows are ``tabIndex=0`` with Enter / Space and carry
 *   ``aria-current`` when selected.
 *
 * Cross-references:
 *   - REQ-UI-118..122 (results table behaviours); REQ-UI-059 (a11y).
 *   - Feature spec §2.10 / §4.1; ADR-038 §Decision 5.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import {
  type ColumnDef,
  type ColumnSizingState,
  type Row,
  type SortingState,
  type Updater,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  MAX_SORT_COLUMNS,
  type DataTableColumn,
  type SortDirection,
  type SortState,
  moveColumnKey,
  nextSortState,
} from "@/data-table/data-table-model";
import { cn } from "@/lib/utils";

/** Default rendered column width in pixels when a column declares none. */
const DEFAULT_COLUMN_WIDTH = 160;
/** Floor for a resize drag — narrow enough to be useful, wide enough to grab. */
const DEFAULT_MIN_COLUMN_WIDTH = 64;
/** Pixels one Arrow-key press moves a column boundary (keyboard resize). */
const KEYBOARD_RESIZE_STEP = 16;

export interface DataTableProps<TRow> {
  /** The full column set for the selected data type, in declaration order. */
  readonly columns: readonly DataTableColumn<TRow>[];
  /** The current page's rows, already ordered and paged by the server. */
  readonly rows: readonly TRow[];
  /** Stable per-row identity — the record id. */
  readonly rowKey: (row: TRow) => string;
  /** Accessible table name, rendered into a visually-hidden ``<caption>``. */
  readonly caption: string;

  /** The ordered sort specification (index 0 is the primary key). */
  readonly sort: SortState;
  /** Called with the resolved next sort state on a chevron activation. */
  readonly onSortChange: (next: SortState) => void;

  /** Effective left-to-right column order (resolved via ``resolveColumnOrder``). */
  readonly columnOrder: readonly string[];
  /** Called with the next order after a header drop. */
  readonly onColumnOrderChange: (next: readonly string[]) => void;

  /** Currently-visible column keys (min-1 invariant enforced by the caller). */
  readonly visibleColumns: ReadonlySet<string>;

  /** Per-column pixel widths keyed by column key; absent ⇒ the declared width. */
  readonly columnSizing: Readonly<Record<string, number>>;
  /** Called with the next sizing map during / after a resize. */
  readonly onColumnSizingChange: (next: Record<string, number>) => void;

  /** The selected row's key — rendered with ``aria-current``. */
  readonly selectedRowKey?: string | null;
  /** Row activation (click / Enter / Space). Omit for a non-interactive table. */
  readonly onRowActivate?: (row: TRow) => void;

  /** Message rendered in place of rows when the result set is empty. */
  readonly emptyMessage?: string;
  /** Test id root; cells and headers derive their ids from it. */
  readonly testId?: string;
}

/**
 * Render the shared results table.
 *
 * Generic over the row shape — the Query panel passes catalog-derived
 * columns whose ``render`` closes over the row's attribute map, so the
 * table never needs to know the record schema.
 */
/**
 * Pair every row with a React key that is unique on this render.
 *
 * ``rowKey`` is the caller's identity function and is normally injective;
 * when it is not (two rows read the same key), React's documented behaviour
 * for duplicate keys is to duplicate or omit children — and in practice it
 * duplicates, leaving the earlier row's ``<tr>`` in the DOM as an orphan with
 * no handlers after the next render. That is exactly what the Query panel
 * showed on 2026-10-19 when a page of one record type was briefly rendered
 * under another type's key function. The table is the last line of defence:
 * the first row keeps the caller's key, each repeat gets a ``#n`` suffix, and
 * the collision is reported once per render so the caller's key function
 * gets fixed rather than the symptom hidden (CLAUDE.md §5.4).
 */
function uniqueRowKeys<TRow>(
  rows: readonly Row<TRow>[],
  rowKey: (row: TRow) => string,
  testId: string,
): { row: Row<TRow>; key: string }[] {
  const seen = new Map<string, number>();
  let collisions = 0;
  const keyed = rows.map((row) => {
    const base = rowKey(row.original);
    const repeat = seen.get(base) ?? 0;
    seen.set(base, repeat + 1);
    if (repeat > 0) collisions += 1;
    return { row, key: repeat === 0 ? base : `${base}#${repeat}` };
  });
  if (collisions > 0) {
    console.warn(
      `[meridian/data-table] ${testId}: rowKey produced ${collisions} duplicate key(s); ` +
        "suffixed to keep every row mounted — fix the caller's key function",
    );
  }
  return keyed;
}

export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  caption,
  sort,
  onSortChange,
  columnOrder,
  onColumnOrderChange,
  visibleColumns,
  columnSizing,
  onColumnSizingChange,
  selectedRowKey = null,
  onRowActivate,
  emptyMessage = "No records match this query",
  testId = "data-table",
}: DataTableProps<TRow>): React.ReactElement {
  // Transient advisory when a Ctrl/Cmd-click is refused at the sort cap.
  // Presentation state, not chrome — never persisted (see the module
  // docstring's "controlled by design").
  const [sortCapNotice, setSortCapNotice] = useState(false);
  // Retract the advisory as soon as the sort actually changes — including
  // when the PARENT resets it, which is what switching queryable type does.
  // Without this the notice outlives the state it describes: the operator
  // switches type, lands on a single sorted column, and is still being told
  // to "clear one before adding another". Acting on that would be wrong,
  // and a stale warning is exactly the kind of thing CLAUDE.md §8.1 means
  // by operator visibility.
  //
  // Safe against self-cancellation: the refusal path returns WITHOUT
  // calling onSortChange, so `sort` is unchanged and this does not fire.
  useEffect(() => {
    setSortCapNotice(false);
  }, [sort]);
  // The column key currently being dragged by its header label. A ref,
  // not state: the value is only read inside drag handlers, and the
  // dataTransfer payload is the authoritative carrier.
  const draggingKey = useRef<string | null>(null);

  const tanstackColumns = useMemo<ColumnDef<TRow>[]>(
    () =>
      columns.map((col) => ({
        id: col.key,
        header: col.label,
        cell: (ctx) => col.render(ctx.row.original),
        size: col.width ?? DEFAULT_COLUMN_WIDTH,
        minSize: col.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH,
        enableSorting: col.sortable === true,
      })),
    [columns],
  );

  // Meridian's SortState → the library's SortingState. The library never
  // writes this back (no ``onSortingChange``): the chevrons drive
  // :func:`nextSortState` directly and the caller re-renders us with the
  // new prop. We consume it only for ``getIsSorted`` / ``getSortIndex``.
  const sorting = useMemo<SortingState>(
    () => sort.map((rule) => ({ id: rule.key, desc: rule.direction === "desc" })),
    [sort],
  );

  const columnVisibility = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const col of columns) map[col.key] = visibleColumns.has(col.key);
    return map;
  }, [columns, visibleColumns]);

  // The library hands sizing updates through as either a value or an
  // updater function; normalise both into the plain map the caller
  // persists.
  const handleSizingChange = useCallback(
    (updater: Updater<ColumnSizingState>): void => {
      const next = typeof updater === "function" ? updater({ ...columnSizing }) : updater;
      onColumnSizingChange({ ...next });
    },
    [columnSizing, onColumnSizingChange],
  );

  const table = useReactTable<TRow>({
    data: rows as TRow[],
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    // The server sorts and pages across the whole result set (D7 / D4);
    // the library must not re-sort or re-slice the page it is handed.
    manualSorting: true,
    manualPagination: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      sorting,
      columnVisibility,
      columnOrder: [...columnOrder],
      columnSizing: { ...columnSizing },
    },
    onColumnSizingChange: handleSizingChange,
  });

  const activateSort = useCallback(
    (key: string, direction: SortDirection, additive: boolean): void => {
      const next = nextSortState(sort, key, direction, additive);
      if (next === sort) {
        // Refused at the cap — say so rather than swallowing the click
        // (CLAUDE.md §8.1, operator visibility over silent behaviour).
        setSortCapNotice(true);
        return;
      }
      setSortCapNotice(false);
      onSortChange(next);
    },
    [sort, onSortChange],
  );

  const handleDrop = useCallback(
    (targetKey: string, movedKey: string | null): void => {
      draggingKey.current = null;
      if (movedKey === null || movedKey === "") return;
      const next = moveColumnKey(columnOrder, movedKey, targetKey);
      if (next !== columnOrder) onColumnOrderChange(next);
    },
    [columnOrder, onColumnOrderChange],
  );

  const resizeBy = useCallback(
    (key: string, delta: number, currentSize: number, minSize: number): void => {
      const next = Math.max(minSize, currentSize + delta);
      onColumnSizingChange({ ...columnSizing, [key]: next });
    },
    [columnSizing, onColumnSizingChange],
  );

  const headers = table.getHeaderGroups()[0]?.headers ?? [];

  return (
    <div className="flex h-full w-full flex-col" data-testid={testId}>
      {sortCapNotice && (
        <p
          role="status"
          data-testid={`${testId}-sort-cap`}
          className="border-b border-border bg-bg-surface px-3 py-1.5 text-body-sm text-warning"
        >
          Sorting is limited to {MAX_SORT_COLUMNS} columns. Clear one before adding another.
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <table
          className="border-collapse text-body-sm"
          style={{ width: table.getTotalSize(), tableLayout: "fixed" }}
          data-testid={`${testId}-table`}
        >
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 z-10 bg-bg-surface">
            <tr className="border-b border-border text-left text-meta uppercase tracking-wide text-text-secondary">
              {headers.map((header) => {
                const column = columns.find((col) => col.key === header.column.id);
                if (column === undefined) return null;
                const sorted = header.column.getIsSorted();
                const ariaSort: "ascending" | "descending" | "none" =
                  sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none";
                return (
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={column.sortable === true ? ariaSort : undefined}
                    style={{ width: header.getSize() }}
                    title={column.headerTitle}
                    onDragOver={(event) => {
                      // Declaring the drop target is what enables ``drop``.
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const moved = event.dataTransfer.getData("text/plain") || draggingKey.current;
                      handleDrop(column.key, moved);
                    }}
                    className={cn(
                      "relative select-none px-3 py-2 align-bottom",
                      column.align === "right" && "text-right",
                    )}
                    data-testid={`${testId}-header-${column.key}`}
                  >
                    <div
                      className={cn(
                        "flex items-end gap-1",
                        column.align === "right" && "justify-end",
                      )}
                    >
                      <span
                        draggable
                        onDragStart={(event) => {
                          draggingKey.current = column.key;
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", column.key);
                        }}
                        onDragEnd={() => {
                          draggingKey.current = null;
                        }}
                        title="Drag to reorder"
                        className="cursor-grab truncate active:cursor-grabbing"
                        data-testid={`${testId}-header-label-${column.key}`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {column.sortable === true && (
                        <SortChevrons
                          columnKey={column.key}
                          label={column.label}
                          sorted={sorted === false ? null : sorted}
                          ordinal={sort.length > 1 ? header.column.getSortIndex() + 1 : null}
                          onActivate={activateSort}
                          testId={testId}
                        />
                      )}
                    </div>
                    <ResizeHandle
                      columnKey={column.key}
                      label={column.label}
                      isResizing={header.column.getIsResizing()}
                      onPointerResize={header.getResizeHandler()}
                      onKeyboardResize={(delta) =>
                        resizeBy(
                          column.key,
                          delta,
                          header.getSize(),
                          column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH,
                        )
                      }
                      testId={testId}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {uniqueRowKeys(table.getRowModel().rows, rowKey, testId).map(({ row, key }) => {
              const selected = selectedRowKey === key;
              return (
                <tr
                  key={key}
                  tabIndex={onRowActivate === undefined ? undefined : 0}
                  aria-current={selected ? "true" : undefined}
                  onClick={
                    onRowActivate === undefined ? undefined : () => onRowActivate(row.original)
                  }
                  onKeyDown={
                    onRowActivate === undefined
                      ? undefined
                      : (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowActivate(row.original);
                          }
                        }
                  }
                  data-testid={`${testId}-row-${key}`}
                  className={cn(
                    "border-b border-border",
                    selected && "bg-bg-elevated",
                    onRowActivate !== undefined &&
                      "cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const column = columns.find((col) => col.key === cell.column.id);
                    return (
                      <td
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                        className={cn(
                          "truncate px-3 py-2 align-top",
                          column?.align === "right" && "text-right",
                        )}
                        data-testid={`${testId}-cell-${cell.column.id}-${key}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p
            data-testid={`${testId}-empty`}
            className="px-3 py-6 text-center text-body-sm text-text-muted"
          >
            {emptyMessage}
          </p>
        )}
      </div>
    </div>
  );
}

interface SortChevronsProps {
  columnKey: string;
  label: string;
  sorted: SortDirection | null;
  ordinal: number | null;
  onActivate: (key: string, direction: SortDirection, additive: boolean) => void;
  testId: string;
}

/**
 * The per-direction sort chevrons + the multi-sort ordinal badge
 * (REQ-UI-120).
 *
 * Two buttons, not one: activating ▲ sorts ascending, activating ▲ again
 * clears *that column's* sort, and ▼ does the same for descending
 * (architect direction, 2026-07-25). ``aria-pressed`` carries the
 * active/inactive state per button; the ``<th>``'s ``aria-sort`` carries
 * the column-level state. Ctrl/Cmd-click appends the column as a
 * subsequent sort key — the library's ``isMultiSortEvent`` default is
 * Shift, which is not the specified modifier, so the modifier is read
 * here instead.
 *
 * The ordinal badge shows only while more than one column is sorted;
 * a lone "1" beside a single sorted column is noise.
 */
function SortChevrons({
  columnKey,
  label,
  sorted,
  ordinal,
  onActivate,
  testId,
}: SortChevronsProps): React.ReactElement {
  const chevron = (direction: SortDirection): React.ReactElement => {
    const active = sorted === direction;
    const Icon = direction === "asc" ? CaretUp : CaretDown;
    const verb = direction === "asc" ? "ascending" : "descending";
    return (
      <button
        type="button"
        aria-pressed={active}
        aria-label={active ? `Clear ${verb} sort on ${label}` : `Sort ${label} ${verb}`}
        title={
          active
            ? `Clear ${verb} sort (Ctrl/Cmd-click to add a secondary sort)`
            : `Sort ${verb} (Ctrl/Cmd-click to add a secondary sort)`
        }
        onClick={(event) => {
          onActivate(columnKey, direction, event.ctrlKey || event.metaKey);
        }}
        data-testid={`${testId}-sort-${direction}-${columnKey}`}
        className={cn(
          "flex h-3 items-center rounded-sm px-0.5",
          "focus:outline-none focus-visible:ring-1 focus-visible:ring-accent",
          active ? "text-brand" : "text-text-muted opacity-40 hover:opacity-100",
        )}
      >
        <Icon size={8} weight="fill" aria-hidden="true" />
      </button>
    );
  };

  return (
    <span className="flex items-center gap-0.5">
      <span className="flex flex-col">
        {chevron("asc")}
        {chevron("desc")}
      </span>
      {sorted !== null && ordinal !== null && (
        <span
          className="rounded-sm bg-bg-elevated px-1 text-meta tabular-nums text-brand"
          data-testid={`${testId}-sort-ordinal-${columnKey}`}
          aria-label={`Sort priority ${ordinal}`}
        >
          {ordinal}
        </span>
      )}
    </span>
  );
}

interface ResizeHandleProps {
  columnKey: string;
  label: string;
  isResizing: boolean;
  onPointerResize: (event: unknown) => void;
  onKeyboardResize: (delta: number) => void;
  testId: string;
}

/**
 * The drag-resize grip on a column's right boundary (REQ-UI-121).
 *
 * The pointer path is the library's own handler, which covers mouse and
 * touch. The keyboard path is Meridian's: the grip is a focusable
 * ``separator`` that Arrow-Left / Arrow-Right nudge by
 * :data:`KEYBOARD_RESIZE_STEP`, so column width is reachable without a
 * pointer (REQ-UI-059). ``role="separator"`` with ``aria-orientation``
 * is the ARIA-correct role for a resizable boundary.
 */
function ResizeHandle({
  columnKey,
  label,
  isResizing,
  onPointerResize,
  onKeyboardResize,
  testId,
}: ResizeHandleProps): React.ReactElement {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      tabIndex={0}
      onMouseDown={onPointerResize}
      onTouchStart={onPointerResize}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          onKeyboardResize(-KEYBOARD_RESIZE_STEP);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          onKeyboardResize(KEYBOARD_RESIZE_STEP);
        }
      }}
      data-testid={`${testId}-resize-${columnKey}`}
      className={cn(
        "absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none",
        "hover:bg-brand focus:outline-none focus-visible:bg-brand",
        isResizing && "bg-brand",
      )}
    />
  );
}
